import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { User } from '../types';

interface AuthContextProps {
  token: string | null;
  role: string | null;
  username: string | null;
  realName: string | null;
  avatar: string | null;
  teamId: string | null;
  teamCode: string | null;
  /** 展示用团队名（登录接口或 /me 同步）；无团队非超管时为「演示团队」 */
  teamName: string | null;
  /** 顶栏主标题：优先 teamName，否则无团队且非超管为演示团队 */
  teamDisplayName: string | null;
  /** 无 teamId 的普通用户：仅可浏览演示数据，写入接口将被拒绝 */
  isDemoBrowseOnly: boolean;
  user: User | null;
  isFirstLogin: boolean;
  passwordChanged: boolean;
  login: (
    token: string,
    role: string,
    username: string,
    realName?: string,
    avatar?: string,
    isFirstLogin?: boolean,
    passwordChanged?: boolean,
    userId?: string,
    teamId?: string,
    teamCode?: string,
    teamName?: string
  ) => void;
  logout: () => void;
  updateUserInfo: (realName?: string, avatar?: string) => void;
}

const AuthContext = createContext<AuthContextProps>({
  token: null,
  role: null,
  username: null,
  realName: null,
  avatar: null,
  teamId: null,
  teamCode: null,
  teamName: null,
  teamDisplayName: null,
  isDemoBrowseOnly: false,
  user: null,
  isFirstLogin: false,
  passwordChanged: false,
  login: () => {},
  logout: () => {},
  updateUserInfo: () => {},
});

function resolveTeamDisplayName(
  teamName: string | null,
  teamId: string | null,
  role: string | null
): string | null {
  if (teamName) return teamName;
  if (!teamId && role && role !== '超级管理员') return '演示团队';
  return null;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [realName, setRealName] = useState<string | null>(null);
  const [avatar, setAvatar] = useState<string | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [teamCode, setTeamCode] = useState<string | null>(null);
  const [teamName, setTeamName] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isFirstLogin, setIsFirstLogin] = useState<boolean>(false);
  const [passwordChanged, setPasswordChanged] = useState<boolean>(false);

  const teamDisplayName = useMemo(
    () => resolveTeamDisplayName(teamName, teamId, role),
    [teamName, teamId, role]
  );

  const isDemoBrowseOnly = useMemo(
    () => Boolean(role && role !== '超级管理员' && role !== '商家' && !teamId),
    [role, teamId]
  );

  const syncTeamNameFromMe = async (authToken: string) => {
    try {
      const res = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!res.ok) return;
      const data = (await res.json()) as {
        team?: { name?: string };
        teamId?: string | null;
        role?: string;
      };
      const t = data.team?.name;
      const fallback =
        !data.teamId && data.role !== '超级管理员' ? '演示团队' : null;
      const next = t || fallback;
      if (next) {
        setTeamName(next);
        localStorage.setItem('teamName', next);
      } else {
        setTeamName(null);
        localStorage.removeItem('teamName');
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    setToken(localStorage.getItem('token'));
    setRole(localStorage.getItem('role'));
    setUsername(localStorage.getItem('username'));
    setRealName(localStorage.getItem('realName'));
    setAvatar(localStorage.getItem('avatar'));
    setTeamId(localStorage.getItem('teamId'));
    setTeamCode(localStorage.getItem('teamCode'));
    setTeamName(localStorage.getItem('teamName'));

    const initToken = localStorage.getItem('token');
    const initRole = localStorage.getItem('role');
    const initUsername = localStorage.getItem('username');
    const initRealName = localStorage.getItem('realName');
    const tid = localStorage.getItem('teamId');

    if (initToken && initRole && initUsername) {
      const userId = localStorage.getItem('userId');
      setUser({
        _id: userId || undefined,
        id: userId || initUsername,
        teamId: tid || undefined,
        username: initUsername,
        name: initRealName || initUsername,
        role: initRole as '超级管理员' | '管理员' | '普通用户',
        studentId: initUsername,
        gender: '男',
      });
      if (initToken) {
        syncTeamNameFromMe(initToken);
      }
    }
  }, []);

  const login = (
    tokenVal: string,
    roleVal: string,
    usernameVal: string,
    realNameVal?: string,
    avatarVal?: string,
    isFirstLoginVal?: boolean,
    passwordChangedVal?: boolean,
    userId?: string,
    nextTeamId?: string,
    nextTeamCode?: string,
    nextTeamName?: string
  ) => {
    setToken(tokenVal);
    setRole(roleVal);
    setUsername(usernameVal);
    setRealName(realNameVal || null);
    setAvatar(avatarVal || null);
    setTeamId(nextTeamId || null);
    setTeamCode(nextTeamCode || null);
    setIsFirstLogin(isFirstLoginVal || false);
    setPasswordChanged(passwordChangedVal || false);

    let nameToStore: string | null = null;
    if (nextTeamName) {
      nameToStore = nextTeamName;
    } else if (nextTeamId) {
      nameToStore = null;
    } else if (roleVal !== '超级管理员') {
      nameToStore = '演示团队';
    }
    setTeamName(nameToStore);
    if (nameToStore) {
      localStorage.setItem('teamName', nameToStore);
    } else {
      localStorage.removeItem('teamName');
    }

    setUser({
      _id: userId,
      id: userId || usernameVal,
      teamId: nextTeamId,
      username: usernameVal,
      name: realNameVal || usernameVal,
      role: roleVal as '超级管理员' | '管理员' | '普通用户',
      studentId: usernameVal,
      gender: '男',
    });

    localStorage.setItem('token', tokenVal);
    localStorage.setItem('role', roleVal);
    localStorage.setItem('username', usernameVal);
    if (realNameVal) localStorage.setItem('realName', realNameVal);
    if (avatarVal) localStorage.setItem('avatar', avatarVal);
    if (userId) localStorage.setItem('userId', userId);
    if (nextTeamId) localStorage.setItem('teamId', nextTeamId);
    else localStorage.removeItem('teamId');
    if (nextTeamCode) localStorage.setItem('teamCode', nextTeamCode);
    else localStorage.removeItem('teamCode');

    if (nextTeamId && !nextTeamName) {
      syncTeamNameFromMe(tokenVal);
    }
  };

  const updateUserInfo = (realNameVal?: string, avatarVal?: string) => {
    if (realNameVal !== undefined) {
      setRealName(realNameVal);
      if (realNameVal) {
        localStorage.setItem('realName', realNameVal);
      } else {
        localStorage.removeItem('realName');
      }
    }
    if (avatarVal !== undefined) {
      setAvatar(avatarVal);
      if (avatarVal) {
        localStorage.setItem('avatar', avatarVal);
      } else {
        localStorage.removeItem('avatar');
      }
    }
  };

  const logout = () => {
    setToken(null);
    setRole(null);
    setUsername(null);
    setRealName(null);
    setAvatar(null);
    setTeamId(null);
    setTeamCode(null);
    setTeamName(null);
    setUser(null);
    setIsFirstLogin(false);
    setPasswordChanged(false);
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('username');
    localStorage.removeItem('realName');
    localStorage.removeItem('avatar');
    localStorage.removeItem('userId');
    localStorage.removeItem('teamId');
    localStorage.removeItem('teamCode');
    localStorage.removeItem('teamName');
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        role,
        username,
        realName,
        avatar,
        teamId,
        teamCode,
        teamName,
        teamDisplayName,
        isDemoBrowseOnly,
        user,
        isFirstLogin,
        passwordChanged,
        login,
        logout,
        updateUserInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
